// Date helpers for the Visitor module — RON-5466 Phase 2.
//
// Module-local, following the precedent of `src/modules/Map/dateUtils.js`. Anything genuinely
// app-wide (`formatDate`, `formatDateToEEEddMMM`) still comes from `src/utils/CommonUtils.js`.
//
// All dates are handled as `YYYY-MM-DD` strings, matching `tblvisitor_visit.start_date` /
// `end_date`, and are built from local date parts rather than `toISOString()` — the latter
// converts to UTC and can shift the day for anyone east or west of Greenwich.

import { formatDateToLong } from "../../utils/CommonUtils.js";

// Compliance dates must carry the YEAR. The house `formatDateToEEEddMMM` renders "Wed 30 Jun",
// which is fine for a visit happening this week and ambiguous for a vetting clearance or an
// insurance expiry — those routinely sit a year or two out, and "30 Jun" could be either.
//
// `formatDateToLong` gives "30 June 2027" with translated month names, but takes DD/MM/YYYY, so
// this bridges from the ISO strings the module stores everywhere else.
export const formatISOToLong = (iso, t) => {
  const [year, month, day] = String(iso || "").slice(0, 10).split("-");

  if (!year || !month || !day) return "";

  return formatDateToLong(`${day}/${month}/${year}`, t);
};

// Hard ceiling on a single visit, mirroring the prototype's 90-iteration guard. Stops a typo in
// the end date (e.g. year 2206) from generating a runaway list of day rows.
export const MAX_VISIT_DAYS = 90;

export const toISODate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// Parses `YYYY-MM-DD` at local midnight. `new Date("2026-04-07")` is parsed as UTC by spec, which
// lands on the previous evening in negative-offset timezones.
export const fromISODate = (iso) => {
  if (typeof iso !== "string") return null;

  const parts = iso.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;

  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);

  return Number.isNaN(date.getTime()) ? null : date;
};

// Every day in an inclusive range, as ISO strings. Returns [] for a missing start; an end before
// the start collapses to the single start day rather than erroring.
export const daysOf = (startISO, endISO) => {
  const start = fromISODate(startISO);
  if (!start) return [];

  const end = fromISODate(endISO) || start;
  const last = end < start ? start : end;

  const out = [];
  const cursor = new Date(start);

  while (cursor <= last && out.length < MAX_VISIT_DAYS) {
    out.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
};

export const isSameOrBefore = (aISO, bISO) => {
  const a = fromISODate(aISO);
  const b = fromISODate(bISO);

  return !a || !b ? false : a.getTime() <= b.getTime();
};
